/**
 * Inline root-cause diagnostic section for a failed run (rendered in the run
 * detail page's analysis column).
 *
 * Shows a trigger button, loading state, completed report, or retry button
 * depending on the diagnostic status.
 */
import React from 'react';
import { Button, Flex, LoadingOrbit, Text, ArrowClockwise } from '@chatsdet/ui';
import { MagnifyingGlass, FlagBanner } from '@phosphor-icons/react';
import type { FailureDiagnostic } from '@chatsdet/types/issues';
import { DiagnosticReport } from '../../../components/issues/tabs/DiagnosticReport';
import type { OpenWorkflowIssueSummary } from '../../flow-list/types';

type FailureDiagnosticSectionProps = {
  status: string | null;
  diagnostic: FailureDiagnostic | null;
  onTrigger: () => void;
  isTriggering: boolean;
  /** Issue already linked to this run's test run, if any; drives the "View issue" affordance. */
  linkedIssueId?: string | null;
  /** Opens the linked issue (e.g. the run detail page's issue drawer). */
  onViewIssue?: () => void;
  /**
   * The workflow's currently open issues (see `useWorkflowOpenIssues`). When
   * there's more than one, renders each as a link instead of the single
   * "View Issue" button — the failure-diagnostic agent now files discrete
   * issues per flow, so a streak can have more than one open at a time.
   */
  openIssues?: OpenWorkflowIssueSummary[];
};

export function FailureDiagnosticSection({
  status,
  diagnostic,
  onTrigger,
  isTriggering,
  linkedIssueId,
  onViewIssue,
  openIssues,
}: FailureDiagnosticSectionProps) {
  // No diagnostic exists yet - show trigger button
  if (!status) {
    return (
      <div className="p-4 rounded-lg bg-muted/50 border border-border">
        <Flex align="center" justify="between">
          <div>
            <Text size="sm" className="font-medium">
              Root Cause Analysis
            </Text>
            <Text size="xs" className="text-muted-foreground mt-0.5">
              Run an AI diagnostic to analyze logs, video, and history
            </Text>
          </div>
          <Button
            variant="outline"
            size="sm"
            onPress={onTrigger}
            isDisabled={isTriggering}
          >
            <Flex align="center" gap="xs">
              <MagnifyingGlass className="w-3.5 h-3.5" />
              {isTriggering ? 'Starting...' : 'Run Diagnosis'}
            </Flex>
          </Button>
        </Flex>
      </div>
    );
  }

  // In progress
  if (status === 'pending' || status === 'running') {
    return (
      <div className="p-4 rounded-lg bg-muted/50 border border-border">
        <Flex align="center" gap="sm">
          <LoadingOrbit size="sm" />
          <div>
            <Text size="sm" className="font-medium">
              Analyzing failure...
            </Text>
            <Text size="xs" className="text-muted-foreground mt-0.5">
              This typically takes 2-3 minutes
            </Text>
          </div>
        </Flex>
      </div>
    );
  }

  // Failed
  if (status === 'failed') {
    return (
      <div className="p-4 rounded-lg bg-muted/50 border border-border">
        <Flex align="center" justify="between">
          <Text size="sm" className="text-muted-foreground">
            Diagnostic analysis was unable to complete.
          </Text>
          <Button variant="outline" size="sm" onPress={onTrigger} isDisabled={isTriggering}>
            <Flex align="center" gap="xs">
              <ArrowClockwise className="w-3.5 h-3.5" />
              {isTriggering ? 'Retrying...' : 'Retry'}
            </Flex>
          </Button>
        </Flex>
      </div>
    );
  }

  // Completed — report is rendered as first-class primary content in the drawer,
  // so we drop the muted wrapper and the duplicated "Root Cause Analysis" caption.
  // The classification badge inside DiagnosticReport now carries that framing.
  if (status === 'completed' && diagnostic) {
    const hasMultipleOpenIssues = (openIssues?.length ?? 0) > 1;
    return (
      <div className="space-y-3">
        <DiagnosticReport diagnostic={diagnostic} />
        {linkedIssueId && hasMultipleOpenIssues && openIssues ? (
          <Flex direction="column" align="end" gap="xs">
            <Text size="xs" className="text-muted-foreground">
              {openIssues.length} open issues on this flow
            </Text>
            {openIssues.map((issue) => (
              <a
                key={issue.id}
                href={`/issues?issueId=${issue.id}`}
                className="text-sm text-primary hover:underline"
              >
                <span className="font-mono text-xs text-muted-foreground mr-1">
                  {issue.shortcode}
                </span>
                {issue.title}
              </a>
            ))}
          </Flex>
        ) : linkedIssueId ? (
          onViewIssue && (
            <Flex justify="end">
              <Button variant="outline" size="sm" onPress={onViewIssue}>
                <Flex align="center" gap="xs">
                  <FlagBanner className="w-3.5 h-3.5" />
                  View Issue
                </Flex>
              </Button>
            </Flex>
          )
        ) : null}
      </div>
    );
  }

  return null;
}
